// client/src/coms/views/canvas_parts/CanvasPaperSurfaceSubmenu.js
// File > Paper & Surface ▸ flyout. Picks the document paper (the same surface
// the Paper tab edits) without touching the active tool, and opens the Paper
// tab for the full surface controls. Mouse: hover or click; touch: tap;
// keyboard: Right/Enter/Space opens, Up/Down/Home/End move, Left/Escape close.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SURFACE_LIBRARY, SURFACE_PICKER_ORDER } from '../../canvas/paint-engine/surfaces/SurfaceLibrary';
import { openPaperSettings, setPaperSurface, usePaperSurface } from '../../canvas/paint-engine/integration/paperSurfaceState';

// The File menu ignores pointer and key events inside elements with this
// attribute (the flyout is portalled outside the menu's DOM subtree).
export const CANVAS_MENU_FLYOUT_ATTRIBUTE = 'data-canvas-menu-flyout';

const FLYOUT_GAP = 4;
const VIEWPORT_MARGIN = 8;
const HOVER_OPEN_MS = 120;
const HOVER_CLOSE_MS = 260;

// Beside the trigger (right, else left); stacked under it when neither side fits.
export function getFlyoutPosition(triggerRect, flyoutSize, viewport) {
  const width = Math.min(flyoutSize.width, viewport.width - VIEWPORT_MARGIN * 2);
  const height = Math.min(flyoutSize.height, viewport.height - VIEWPORT_MARGIN * 2);
  const clampTop = (top) => Math.max(VIEWPORT_MARGIN, Math.min(viewport.height - height - VIEWPORT_MARGIN, top));
  const right = triggerRect.right + FLYOUT_GAP;
  if (right + width <= viewport.width - VIEWPORT_MARGIN) return { left: right, top: clampTop(triggerRect.top - 5), side: 'right' };
  const left = triggerRect.left - FLYOUT_GAP - width;
  if (left >= VIEWPORT_MARGIN) return { left, top: clampTop(triggerRect.top - 5), side: 'left' };
  return {
    left: Math.max(VIEWPORT_MARGIN, Math.min(viewport.width - width - VIEWPORT_MARGIN, triggerRect.left)),
    top: clampTop(triggerRect.bottom + FLYOUT_GAP),
    side: 'below',
  };
}

export default function CanvasPaperSurfaceSubmenu({ itemStyle, onDone }) {
  const { surface, available } = usePaperSurface();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const flyoutRef = useRef(null);
  const hoverTimerRef = useRef(0);
  const focusOnOpenRef = useRef(null);
  const lastPointerTypeRef = useRef('');

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = 0;
  };
  useEffect(() => clearHoverTimer, []);

  const getItems = () => (flyoutRef.current
    ? Array.from(flyoutRef.current.querySelectorAll('button[role^="menuitem"]:not([disabled])'))
    : []);

  const openFlyout = useCallback((focus = null) => {
    clearHoverTimer();
    focusOnOpenRef.current = focus;
    setOpen(true);
  }, []);

  const closeFlyout = useCallback((refocusTrigger = false) => {
    clearHoverTimer();
    setOpen(false);
    setPosition(null);
    if (refocusTrigger && triggerRef.current) triggerRef.current.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !flyoutRef.current) return undefined;
    const place = () => {
      if (!triggerRef.current || !flyoutRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition(getFlyoutPosition(rect, {
        width: flyoutRef.current.offsetWidth,
        height: flyoutRef.current.scrollHeight,
      }, { width: window.innerWidth, height: window.innerHeight }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !position || !focusOnOpenRef.current) return;
    const items = getItems();
    const target = focusOnOpenRef.current === 'last'
      ? items[items.length - 1]
      : items.find((item) => item.getAttribute('aria-checked') === 'true') || items[0];
    focusOnOpenRef.current = null;
    if (target) target.focus();
  }, [open, position]);

  const choosePreset = (preset) => {
    // Only the surface changes: the active tool and its settings stay as they are.
    setPaperSurface({ preset });
    closeFlyout();
    if (onDone) onDone();
  };

  const openSettings = (advanced, preset = null) => {
    if (preset) setPaperSurface({ preset });
    openPaperSettings({ advanced });
    closeFlyout();
    if (onDone) onDone();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFlyout('first');
    } else if (event.key === 'ArrowLeft' && open) {
      event.preventDefault();
      closeFlyout(true);
    }
  };

  const handleFlyoutKeyDown = (event) => {
    const items = getItems();
    const index = items.indexOf(document.activeElement);
    const focusAt = (next) => {
      if (!items.length) return;
      items[(next + items.length) % items.length].focus();
    };
    if (event.key === 'ArrowDown') { event.preventDefault(); focusAt(index + 1); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); focusAt(index < 0 ? items.length - 1 : index - 1); }
    else if (event.key === 'Home') { event.preventDefault(); focusAt(0); }
    else if (event.key === 'End') { event.preventDefault(); focusAt(items.length - 1); }
    else if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeFlyout(true);
    } else if (event.key === 'Tab') {
      closeFlyout(true);
    }
  };

  const scheduleHover = (nextOpen) => {
    clearHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = 0;
      if (nextOpen) setOpen(true);
      else closeFlyout();
    }, nextOpen ? HOVER_OPEN_MS : HOVER_CLOSE_MS);
  };

  const flyoutItemStyle = (selected) => ({
    ...itemStyle,
    justifyContent: 'flex-start',
    gap: 8,
    background: selected ? 'rgba(59,130,246,0.18)' : 'transparent',
    opacity: available ? 1 : 0.45,
    cursor: available ? 'pointer' : 'default',
  });

  const flyout = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={flyoutRef}
      role="menu"
      aria-label="Paper & Surface"
      {...{ [CANVAS_MENU_FLYOUT_ATTRIBUTE]: 'true' }}
      data-canvas-no-swipe="true"
      onKeyDown={handleFlyoutKeyDown}
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') clearHoverTimer(); }}
      onPointerLeave={(event) => { if (event.pointerType === 'mouse') scheduleHover(false); }}
      style={{
        position: 'fixed',
        left: position ? position.left : -10000,
        top: position ? position.top : 0,
        visibility: position ? 'visible' : 'hidden',
        zIndex: 100001,
        width: 'max-content',
        minWidth: 228,
        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        boxSizing: 'border-box',
        padding: 5,
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.14)',
        background: 'rgba(42,42,42,0.98)',
        boxShadow: '0 18px 46px rgba(0,0,0,0.48)',
      }}
    >
      {!available ? (
        <div style={{ padding: '5px 9px 7px', maxWidth: 240, whiteSpace: 'normal', color: 'rgba(255,255,255,0.62)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
          Paper &amp; Surface needs the physical paint engine (WebGPU). This document uses plain digital paper.
        </div>
      ) : null}
      {SURFACE_PICKER_ORDER.map((id) => {
        const selected = surface.preset === id;
        return (
          <button
            key={id}
            type="button"
            role="menuitemradio"
            aria-checked={selected}
            disabled={!available}
            data-surface-preset={id}
            onClick={() => choosePreset(id)}
            style={flyoutItemStyle(selected)}
          >
            <span aria-hidden="true" style={{ width: 12, flex: '0 0 12px', color: '#93c5fd', textAlign: 'center' }}>{selected ? '✓' : ''}</span>
            <span>{SURFACE_LIBRARY[id].name}</span>
          </button>
        );
      })}
      <div role="separator" style={{ height: 1, margin: '5px 3px', background: 'rgba(255,255,255,0.12)' }} />
      <button type="button" role="menuitem" disabled={!available} onClick={() => openSettings(true, 'custom')} style={flyoutItemStyle(false)}>
        <span aria-hidden="true" style={{ width: 12, flex: '0 0 12px' }} />
        <span>Custom Surface…</span>
      </button>
      <button type="button" role="menuitem" disabled={!available} onClick={() => openSettings(false)} style={flyoutItemStyle(false)}>
        <span aria-hidden="true" style={{ width: 12, flex: '0 0 12px' }} />
        <span>Surface Settings…</span>
      </button>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="menu-paper-surface"
        onPointerDown={(event) => { lastPointerTypeRef.current = event.pointerType || ''; }}
        onClick={() => {
          // A mouse click never closes a flyout that hovering just opened; taps toggle.
          const mouse = lastPointerTypeRef.current === 'mouse';
          lastPointerTypeRef.current = '';
          if (open && !mouse) closeFlyout();
          else openFlyout();
        }}
        onKeyDown={handleTriggerKeyDown}
        onPointerEnter={(event) => { if (event.pointerType === 'mouse') scheduleHover(true); }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'mouse') return;
          if (open) scheduleHover(false);
          else clearHoverTimer();
        }}
        style={{ ...itemStyle, background: open ? 'rgba(255,255,255,0.08)' : itemStyle.background }}
      >
        <span>Paper &amp; Surface</span>
        <span aria-hidden="true" style={{ color: 'rgba(255,255,255,0.62)' }}>▸</span>
      </button>
      {flyout}
    </>
  );
}
